/**
 * cloud_air — THE CLOUDS' AIR (2026-10-07, the cloud-realism work; the photo references:
 * docs/research/2026-10-07-cloud-realism/). Pure: no three.
 *
 * A cloud is seen through kilometres of air. The photographs show what that does: a cumulus's shaded side is blue-grey
 * (the measured shade of real cumulus is three to four times as blue as ours was), its sunlit side stays white, and with
 * distance the whole cloud loses contrast and sinks, cream over blue-grey, into the horizon. The engine's clouds take the
 * air as an extinction per channel (CloudMarch.cloudAir) — this file derives it from Atlas's ONE atmosphere, so the air
 * in front of a cloud is the air the sky is drawn with:
 *
 *   two exponential layers — the boundary-layer aerosol (grey, 1.2 km scale height) and Rayleigh with the background
 *   aerosol (blue first, 8 km) — integrated along each sight line: the air thins with height, so a storm's anvil 6 km
 *   up keeps its edge where the cumulus bases under it have gone into the haze.
 *
 * The light the air adds is the horizon's own radiance (the scene fog's colour: an endless level path through this air
 * IS the horizon), so over a short path it comes out blue — blue saturates first — and over a long one the horizon's white.
 */
import type { AtmosphereConfig } from 'enginex/render/sky/AtmosphereConfig.js';
import { ATLAS_ATMOSPHERE } from './atmosphere.js';

export interface AirBeta { r: number; g: number; b: number }
/** Two exponential layers: a SEA-LEVEL extinction per metre per channel and a scale height (m) each (the engine's `setAir`). */
export interface CloudAirLayers { low: AirBeta; lowScaleM: number; high: AirBeta; highScaleM: number }

/** The heap clouds' mid height over the ground (m): where a sight line to a cumulus ends. */
export const CLOUD_AIR_TOP_M = 1200;

/**
 * Atlas's clear air as the clouds take it: the boundary-layer aerosol (grey, 1.2 km), and Rayleigh with the background
 * aerosol (blue first, 8 km). `aerosol` false leaves the haze out (a caller whose fog already carries it). Ozone is
 * nothing this low.
 */
export function cloudAirLayers(aerosol = true, cfg: AtmosphereConfig = ATLAS_ATMOSPHERE): CloudAirLayers {
  const f = aerosol ? Math.max(0, Math.min(1, cfg.mieBackgroundFraction ?? 0)) : 0;
  const m = aerosol ? 1 : 0;
  const k = 1 / 1000; // per km → per metre
  return {
    low: { r: cfg.mieExtinction.x * (1 - f) * m * k, g: cfg.mieExtinction.y * (1 - f) * m * k, b: cfg.mieExtinction.z * (1 - f) * m * k },
    lowScaleM: cfg.mieScaleHeight * 1000,
    high: { r: (cfg.rayleighScattering.x + cfg.mieExtinction.x * f) * k, g: (cfg.rayleighScattering.y + cfg.mieExtinction.y * f) * k, b: (cfg.rayleighScattering.z + cfg.mieExtinction.z * f) * k },
    highScaleM: cfg.rayleighScaleHeight * 1000,
  };
}

/**
 * The optical depth per channel along a sight line from an eye at `eyeAltM` to a point `distM` away and `riseM` higher —
 * the CPU mirror of the shader (CloudMarch.cloudAir): each layer's β₀·e^(−h₀/H)·d·(1 − e^(−a))/a, a = rise / H.
 */
export function cloudAirTau(L: CloudAirLayers, eyeAltM: number, riseM: number, distM: number): AirBeta {
  const h0 = Math.max(0, Number.isFinite(eyeAltM) ? eyeAltM : 0), d = Math.max(0, Number.isFinite(distM) ? distM : 0);
  const rise = Number.isFinite(riseM) ? riseM : 0;
  const one = (scale: number): number => {
    const a = Math.max(-2, Math.min(40, rise / scale));
    const mean = Math.abs(a) < 1e-3 ? 1 : (1 - Math.exp(-a)) / a;
    return Math.exp(-h0 / scale) * d * mean;
  };
  const lo = one(L.lowScaleM), hi = one(L.highScaleM);
  return { r: L.low.r * lo + L.high.r * hi, g: L.low.g * lo + L.high.g * hi, b: L.low.b * lo + L.high.b * hi };
}

/** What is left of a cloud's own light behind an optical depth (per channel). */
export function cloudAirTransmittance(tau: AirBeta): AirBeta {
  return { r: Math.exp(-tau.r), g: Math.exp(-tau.g), b: Math.exp(-tau.b) };
}

/** The kill switch (`localStorage atlas:cloud-air = 'off'`): the clouds without their air. Read once. */
let airFlag: boolean | null = null;
export function cloudAirOn(): boolean {
  if (airFlag === null) { try { airFlag = globalThis.localStorage?.getItem('atlas:cloud-air') !== 'off'; } catch { airFlag = true; } }
  return airFlag;
}
